import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldInput from '../FieldInput';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TableTextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    value: PropTypes.array,
    cellValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rowId: PropTypes.string,

    calculatedCellValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    const { cellValue } = props;
    this.state = { focused: false, changes: cellValue };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { cellValue } = this.props;
    if (cellValue !== prevProps.cellValue && cellValue !== changes) this.setState({ changes: cellValue });
  }

  componentWillUnmount() {

    const { cellValue, disabled, fieldId, fieldIdentifier, fieldPositionId, rowId, columnIdentifier, column, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== cellValue && !disabled) {

      this.handleCellChange(changes);

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);
    }

  }

  render() {

    const { focused, changes } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      rowId,
      column,
      columnIdentifier,

      cellValue,
      calculatedCellValue,
      disabled,
      tabIndex,

      //Field style
      primaryColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onFocus,
      onBlur,

      fieldMode,
      fieldPresentationMode,
      pageLayoutMode

    } = this.props;

    const displayInput = focused ? (
      <FieldInput
        autoFocus
        key={`${fieldPositionId}-table-text-field`}
        data-testid={`${fieldPositionId}-table-text-field`}
        tabIndex={tabIndex}
        value={changes}
        type="text"
        {...{
          primaryColor,
          disabled,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          textOverflow,
          borderColor,
          borderRadius,
          borderWidth,
          backgroundColor,
          padding,

        }}
        onChange={(e) => {
          this.setState({ changes: e.target.value });
        }}
        onFocus={(e) => {

          if (!focused) this.setState({ focused: true });

          if (!disabled && onFocus) {
            onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } }, rowId, column, columnIdentifier);
          }

        }}
        onBlur={(e) => {

          const nextValue = e.target.value;

          /**
           * The prop is the source of truth once the edit is dispatched: a cleared cell under a
           * column formula (NO-2347) edits as that same formula again on the next focus.
           */
          if (focused) this.setState({ focused: false, changes: cellValue });

          if (!disabled && nextValue !== cellValue) this.handleCellChange(nextValue);

          if (onBlur) {

            onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);

          }
        }}
      />
    ) : (
      <FieldInput
        key={`${fieldPositionId}-table-text-field-calculated`}
        data-testid={`${fieldPositionId}-table-text-field-calculated`}
        tabIndex={tabIndex}
        value={calculatedCellValue ?? ''} //?? so a stored 0 is not swallowed
        type="text"
        {...{

          disabled,
          primaryColor,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          textOverflow,
          borderColor,
          borderRadius,
          borderWidth,
          backgroundColor,
          padding,
          fieldMode,
          fieldPresentationMode,
          pageLayoutMode

        }}
        onFocus={() => {
          if (!focused) this.setState({ focused: true });
        }}
        onChange={() => {
          /**
           *<-----------------NOTICE------------------>
           * This handler was added to fix this react warningYou provided a `value` prop to a form field
           * without an `onChange` handler. This will render a read-only field. If the field should
           * be mutable use `defaultValue`. Otherwise, set either `onChange` or `readOnly`.
          */
        }}
      />
    );

    return (
      <StyledWrapper {...{ margin }}>
        {displayInput}
      </StyledWrapper>
    );
  }

  handleCellChange = (nextCellValue) => {

    const { onChange, rowId, column, value, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const nextValue = [...value];

    const targetRowIndex = value.findIndex((row) => row._id === rowId);
    const nextRow = { ...nextValue[targetRowIndex] };

    /**<----------NOTICE---------------->
     * onChange will only be triggered if the row and column associated
     * with the table cell is undeleted
    */
    if (nextRow && nextRow._id) {

      nextRow.cells = { ...nextRow.cells, [column]: nextCellValue };

      nextValue[targetRowIndex] = nextRow;

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

    }
    else {
      this.setState({ changes: '' });
    }

  }

}

export default TableTextField;
